import { IonCheckbox } from '@ionic/react'
import type { UserSettings } from '@vae/server/dist/client'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ResourceType } from '../../../@types/i18n'
import { logEvent } from '../../../analytics/utils/firebaseUtils'
import useUser from '../../../contexts/hooks/useUser'
import { trpc } from '../../../modules/trpc'
import PageWrapper from '../../../wrappers/pageWrapper/PageWrapper'
import { NotificationSettingsSkeleton } from './components/NotificationSettingsSkeleton'

interface NotificationOption {
	key: keyof ResourceType['pages_settings']['notificationSettingsPage']
	push?: boolean
	email?: boolean
}

const NotificationSettings = () => {
	const utils = trpc.useContext()

	const { user, userSettings: settings, isLoading } = useUser()

	const editSettings = trpc.user.userSettings.edit.useMutation({})

	const { t } = useTranslation('pages_settings', { keyPrefix: 'notificationSettingsPage' })

	const [options, setOptions] = useState<NotificationOption[]>([])

	useEffect(() => {
		if (settings) {
			setOptions([
				{
					key: 'weeklyNews',
					push: settings.weeklyNewsNotisEnabled,
					email: settings.weeklyNewsEmailEnabled,
				},
				{
					key: 'hourlyTracker',
					push: settings.hourlyTrackerNotisEnabled,
					email: settings.hourlyTrackerEmailEnabled,
				},
				{
					key: 'tomorrowPrices',
					push: settings.tomorrowPricesNotisEnabled,
					email: settings.tomorrowPricesEmailEnabled,
				},
				{
					key: 'monthlyReport',
					push: settings.monthlyReportNotisEnabled,
					email: settings.monthlyReportEmailEnabled,
				},
				{
					key: 'newInvoiceAvailable',
					push: settings.newInvoiceAvailableNotisEnabled,
					email: settings.newInvoiceAvailableEmailEnabled,
				},
				{
					key: 'smartChargingInfo',
					push: settings.smartChargingInfoNotisEnabled,
					email: settings.smartChargingInfoEmailEnabled,
				},
				{
					key: 'tomorrowPricesInfo',
					email: settings.tomorrowPricesInfoEmailEnabled,
					push: undefined,
				},
			])
		}
	}, [settings, t])

	const handleToggleChange = (
		i: number,
		enabled: boolean,
		label: string,
		type: 'email' | 'push'
	) => {
		logEvent('click-notifications-toggle', { value: label })

		setOptions(prev => prev.map((opt, idx) => (idx === i ? { ...opt, [type]: enabled } : opt)))

		const notificationKeyMap: Record<
			keyof Omit<ResourceType['pages_settings']['notificationSettingsPage'], 'app' | 'email'>,
			{ email?: keyof UserSettings; push?: keyof UserSettings }
		> = {
			weeklyNews: {
				email: 'weeklyNewsEmailEnabled',
				push: 'weeklyNewsNotisEnabled',
			},
			hourlyTracker: {
				email: 'hourlyTrackerEmailEnabled',
				push: 'hourlyTrackerNotisEnabled',
			},
			tomorrowPrices: {
				email: 'tomorrowPricesEmailEnabled',
				push: 'tomorrowPricesNotisEnabled',
			},
			monthlyReport: {
				email: 'monthlyReportEmailEnabled',
				push: 'monthlyReportNotisEnabled',
			},
			newInvoiceAvailable: {
				email: 'newInvoiceAvailableEmailEnabled',
				push: 'newInvoiceAvailableNotisEnabled',
			},
			smartChargingInfo: {
				email: 'smartChargingInfoEmailEnabled',
				push: 'smartChargingInfoNotisEnabled',
			},
			tomorrowPricesInfo: {
				email: 'tomorrowPricesInfoEmailEnabled',
			},
		}

		const optionKey = options[i].key as keyof typeof notificationKeyMap
		const key: keyof UserSettings | undefined = notificationKeyMap[optionKey][type]

		if (!key) return

		editSettings.mutate(
			{
				userId: user!.id,
				data: {
					[key]: enabled,
				},
			},
			{
				onSuccess: () => {
					utils.user.userSettings.invalidate()
				},
			}
		)
	}

	if (isLoading) return <NotificationSettingsSkeleton />

	return (
		<PageWrapper page='notificationSettings' appBarOptions={{ backPath: '/settings' }}>
			<div className='options'>
				<div className='flex pb-[0.6rem] justify-end gap-1 pr-[14px] items-center text-center text-text-dark dark:text-white'>
					<div className='w-[72px]'>{t('email')}</div>
					<div className='w-[72px]'>{t('app')}</div>
				</div>

				{options.map((option, i) => (
					<div
						key={option.key}
						className='row border-t border-text-grey-100 dark:border-brand-secondary-200-dark flex justify-between items-center pr-[14px]'
					>
						<div className='text-text-dark dark:text-white'>{t(option.key)}</div>
						<div className='flex gap-1'>
							<div className='w-[72px] flex justify-center'>
								{option.email !== undefined && (
									<IonCheckbox
										className='m-0 dark:[--border-color:#3cb49f] dark:[--border-color-checked:#3cb49f] dark:[--checkbox-background-checked:#3cb49f] dark:[--checkbox-background:#144448] [--border-color:#52ae32] [--border-color-checked:#52ae32] [--checkbox-background-checked:#52ae32] [--checkbox-background:#cae3cc]'
										checked={option.email}
										onIonChange={e =>
											handleToggleChange(i, e.detail.checked, t(option.key), 'email')
										}
									/>
								)}
							</div>
							<div className='w-[72px] flex justify-center'>
								{option.push !== undefined && (
									<IonCheckbox
										className='m-0 dark:[--border-color:#3cb49f] dark:[--border-color-checked:#3cb49f] dark:[--checkbox-background-checked:#3cb49f] dark:[--checkbox-background:#144448] [--border-color:#52ae32] [--border-color-checked:#52ae32] [--checkbox-background-checked:#52ae32] [--checkbox-background:#cae3cc]'
										checked={option.push}
										onIonChange={e =>
											handleToggleChange(i, e.detail.checked, t(option.key), 'push')
										}
									/>
								)}
							</div>
						</div>
					</div>
				))}
			</div>
		</PageWrapper>
	)
}

export default NotificationSettings
